/******/ (() => { // webpackBootstrap
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // Bind handlers once
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClearButtonVisibility = this.handleClearButtonVisibility.bind(this);
    this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
    this.resetState = this.resetState.bind(this);

    // State
    this.searchQuery = '';
    this.searchChangeTimer = null;

    // Will be set in bindEvents()
    this.input = null;
    this.submitButton = null;
    this.clearButton = null;
    this.resultsContainer = null;
    this.emptyStateContainer = null;
    this.boxShadowElement = null;
    this.isPredictive = false;

    this._psObserver = null;
  }

  connectedCallback() {
    window.wetheme?.webcomponentRegistry?.register?.({ key: 'component-predictive-search' });

    // Initial bind (and safe even if drawer changes later)
    this.bindEvents();

    // Observe subtree changes (Flow can replace input in mobile drawer)
    if (!this._psObserver) {
      this._psObserver = new MutationObserver(() => {
        const currentInput = this.querySelector('[data-predictive-search-input]');
        if (currentInput && currentInput !== this.input) {
          this.bindEvents();
        }
      });

      this._psObserver.observe(this, { childList: true, subtree: true });
    }
  }

  bindEvents() {
    // Re-query every time (important on mobile)
    this.input = this.querySelector('[data-predictive-search-input]');
    this.submitButton = this.querySelector('[data-predictive-search-submit]');
    this.clearButton = this.querySelector('[data-predictive-search-clear]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.emptyStateContainer = this.querySelector('[data-predictive-search-empty-state]');
    this.boxShadowElement = this.closest('.popup-shadow');

    this.isPredictive = this.getAttribute('is-predictive') === 'true';
    this.searchQuery = this.input ? this.input.value : '';

    if (!this.input) return;

    // Input events
    this.input.removeEventListener('input', this.handleInputChange);
    this.input.addEventListener('input', this.handleInputChange);

    // Extra safety for some mobile edge cases
    this.input.removeEventListener('keyup', this.handleInputChange);
    this.input.addEventListener('keyup', this.handleInputChange);

    this.input.removeEventListener('change', this.handleInputChange);
    this.input.addEventListener('change', this.handleInputChange);

    this.input.removeEventListener('keydown', this.handleInputKeyDown);
    this.input.addEventListener('keydown', this.handleInputKeyDown);

    // Prevent form navigation on iOS keyboard "Search/Return" button tap
    this.form = this.querySelector('.predictive-search__form');
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
      this.form.addEventListener('submit', this.handleSubmit);
    }

    // Buttons (optional in some variants)
    if (this.submitButton) {
      this.submitButton.removeEventListener('click', this.handleSubmit);
      this.submitButton.addEventListener('click', this.handleSubmit);
    }

    if (this.clearButton) {
      this.clearButton.removeEventListener('click', this.handleClear);
      this.clearButton.addEventListener('click', this.handleClear);
    }
  }

  handleInputChange() {
    this.handleClearButtonVisibility();

    if (!this.isPredictive) return;
    if (!this.input) return;

    const val = this.input.value || '';

    if (val !== '') {
      this.searchQuery = val;

      if (this.searchChangeTimer) {
        window.clearTimeout(this.searchChangeTimer);
      }

      this.searchChangeTimer = window.setTimeout(() => {
        this.performSearch(this.searchQuery);
        this.searchChangeTimer = null;
      }, 250);
    } else {
      this.resetState();
    }
  }

  handleClearButtonVisibility() {
    if (!this.clearButton || !this.input) return;

    if (this.input.value === '') {
      this.clearButton.classList.add('fade-out');
      setTimeout(() => {
        this.clearButton.classList.add('hide');
        this.clearButton.classList.remove('fade-out');
      }, 250);
    } else {
      this.clearButton.classList.remove('hide');
    }
  }

  handleInputKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleSubmit(event);
    }
  }

  // Always go to Hebrew root search (strip /en from root if present)
  handleSubmit(event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();

    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
        ? window.Shopify.routes.root
        : '/';

    const heRoot = root.replace(/^\/en\/?/, '/');
    const q = this.input ? this.input.value : '';
    window.location.href = `${heRoot}search?q=${encodeURIComponent(q)}*`;
  }

  handleClear() {
    if (!this.input) return;

    this.input.value = '';
    this.searchQuery = '';
    this.handleClearButtonVisibility();
    this.input.focus();
    this.resetState();
  }

  async performSearch(query) {
    if (!this.resultsContainer) return;

    const token = (window.wetheme && window.wetheme.storefrontToken) || '28c8b26579386ef3c54bd9aebce39418';
    if (!token) return;

    const gql = `
      query SearchQuery($query: String!) {
        products(first: 10, query: $query, sortKey: RELEVANCE) {
          edges {
            node {
              title handle onlineStoreUrl
              priceRange { minVariantPrice { amount } }
              featuredImage { url altText }
            }
          }
        }
      }
    `;

    let searchData;
    try {
      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: gql, variables: { query } }),
      });
      if (!response.ok) return;
      const json = await response.json();
      searchData = json.data;
    } catch (_) { return; }

    if (!this.input || this.input.value === '') return;
    if (!searchData) return;

    const toPath = url => { try { return url ? new URL(url).pathname : '#'; } catch (_) { return url || '#'; } };

    const products = ((searchData.products && searchData.products.edges) || []).map(e => ({
      title: e.node.title,
      url: toPath(e.node.onlineStoreUrl),
      featured_image: e.node.featuredImage ? { url: e.node.featuredImage.url } : null,
      price: Math.round(parseFloat(e.node.priceRange.minVariantPrice.amount) * 100),
    }));

    const collections = [];

    if (this.emptyStateContainer) this.emptyStateContainer.setAttribute('hidden', '');

    const hasResults = products.length || collections.length;
    this.renderSearchResults(
      hasResults
        ? this._buildResultsHTML(products, collections, [], query)
        : this._buildNoResultsHTML()
    );
  }

  _formatMoney(cents) {
    const fmt = (window.wetheme && window.wetheme.moneyFormat) ? window.wetheme.moneyFormat : '{{ amount }}';
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return fmt.replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  _buildNoResultsHTML() {
    const t = (window.wetheme && window.wetheme.translations) ? window.wetheme.translations : {};
    const msg = t.no_results_html || 'אין תוצאות';
    return `<div class="predictive-search__dropdown"><p class="predictive-search__dropdown-no-results">${msg}</p></div>`;
  }

  _buildResultsHTML(products, collections, queries, query) {
    const allItems = [];

    products.forEach(p => {
      if (allItems.length < 10) allItems.push({
        title: p.title,
        url: p.url,
        image: p.featured_image ? p.featured_image.url : null,
      });
    });
    collections.forEach(c => {
      if (allItems.length < 10) allItems.push({
        title: c.title,
        url: c.url,
        image: c.featured_image ? c.featured_image.url : null,
      });
    });

    const itemsHtml = allItems.map(item => {
      const imgTag = item.image
        ? `<img class="predictive-search__dropdown-item-img" src="${item.image}?width=80" alt="${item.title.replace(/"/g, '&quot;')}" loading="lazy">`
        : `<div class="predictive-search__dropdown-item-img predictive-search__dropdown-item-img--placeholder"></div>`;
      return `<a class="predictive-search__dropdown-item" href="${item.url}" dir="auto">${imgTag}<span class="predictive-search__dropdown-item-title">${item.title}</span></a>`;
    }).join('');

    let viewAllHtml = '';
    if (allItems.length >= 5) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
      const heRoot = root.replace(/^\/en\/?/, '/');
      const searchUrl = `${heRoot}search?q=${encodeURIComponent(query)}*`;
      viewAllHtml = `<a class="predictive-search__dropdown-view-all" href="${searchUrl}">לכל התוצאות</a>`;
    }

    return `<div class="predictive-search__dropdown">${itemsHtml}${viewAllHtml}</div>`;
  }

  renderSearchResults(html) {
    if (!this.resultsContainer) return;

    this.resultsContainer.innerHTML = html;

    // Remove any previously tracked event listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }

    this.boundNavButtons = [];

    const navButtons = this.resultsContainer.querySelector('[data-predictive-search-nav-buttons]');
    if (navButtons) {
      navButtons.addEventListener('click', this.handleNavButtonClick);
      this.boundNavButtons.push(navButtons);
    }
  }

  handleNavButtonClick(e) {
    e.stopPropagation();

    const navButton = e.target.closest('[data-nav-id]');
    if (!navButton) return;

    const resultId = navButton.getAttribute('data-nav-id');
    if (!resultId) return;

    const results = this.resultsContainer?.querySelector(`[data-grid-id="${resultId}"]`);
    if (!results) return;

    const previousActiveButton = this.resultsContainer.querySelector(`[data-nav-id].active`);
    const previousActiveResult = this.resultsContainer.querySelector(`[data-grid-id]:not([hidden])`);

    if (previousActiveButton) {
      previousActiveButton.setAttribute('aria-selected', 'false');
      previousActiveButton.classList.remove('active');
    }

    navButton.setAttribute('aria-selected', 'true');
    navButton.classList.add('active');

    const heightContainer = this.resultsContainer.querySelector('[data-predictive-search-main-results]');
    if (!heightContainer) return;

    const beforeHeight = heightContainer.clientHeight;

    if (previousActiveResult) {
      previousActiveResult.setAttribute('hidden', '');
    }

    results.removeAttribute('hidden');

    // Animate height transition (safe even if browser ignores animate)
    try {
      heightContainer.animate(
        [
          { height: `${beforeHeight}px` },
          { height: `${heightContainer.clientHeight}px` }
        ],
        { duration: 500, easing: 'cubic-bezier(0.85, 0, 0.15, 1)' }
      );
    } catch (_) {}
  }

  resetState() {
    if (this.searchChangeTimer) {
      window.clearTimeout(this.searchChangeTimer);
      this.searchChangeTimer = null;
    }

    if (!this.resultsContainer) return;

    if (this.emptyStateContainer) {
      this.emptyStateContainer.removeAttribute('hidden');
      this.renderSearchResults('');
      return;
    }

    this.resultsContainer.classList.add('fade-out');

    if (this.boxShadowElement) {
      this.boxShadowElement.classList.remove('popup-shadow');
    }

    setTimeout(() => {
      this.renderSearchResults('');
      this.resultsContainer.classList.remove('fade-out');

      if (this.boxShadowElement) {
        this.boxShadowElement.classList.add('popup-shadow');
      }
    }, 300);
  }

  disconnectedCallback() {
    if (this._psObserver) {
      this._psObserver.disconnect();
      this._psObserver = null;
    }

    if (this.input) {
      this.input.removeEventListener('input', this.handleInputChange);
      this.input.removeEventListener('keyup', this.handleInputChange);
      this.input.removeEventListener('change', this.handleInputChange);
      this.input.removeEventListener('keydown', this.handleInputKeyDown);
    }

    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit);
    }

    if (this.submitButton) {
      this.submitButton.removeEventListener('click', this.handleSubmit);
    }

    if (this.clearButton) {
      this.clearButton.removeEventListener('click', this.handleClear);
    }

    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
/******/ })();
;
